// src/shared/navigation/SideNav/components/ChangelogModal.tsx
import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import { ReactComponent as IconChangelog } from "@assets/Icons/IconChangelog.svg";

/**
 * Props del modal
 */
interface ChangelogModalProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Múltiples versiones del changelog para forzar scrolling
 */
const changelogData = [
    {
        versionTitle: "Version 2.3.0 (June 18, 2024)",
        description: "We are excited to announce a new update for our dashboard.",
        items: [
            {
                title: "New filter organisation",
                text: `You can now access your subscriptions and the corresponding data 
        more easily from the top menu of the dashboard...`,
            },
            {
                title: "Activity log",
                text: `This new functionality allows you to view changes made in: BGP 
        Neighbor Management, Licensing Management, Instance Management...`,
            },
        ],
    },
    {
        versionTitle: "Version 2.2.0 (April 19, 2024)",
        description:
            "We are excited to announce a new update for our dashboard, focused on our CTX-R.",
        items: [
            {
                title: "CTX-R License Management",
                text: `Users now have the ability to view all their CTX-R licenses 
        from a unified interface...`,
            },
            {
                title: "Associated CTX-R Visualization",
                text: `Users can access a clear and detailed list of all CTX-R...`,
            },
            {
                title: "CTX-R Neighbors Management",
                text: `The ability to add, delete, and edit neighbors to CTX-R with 
        just a few clicks...`,
            },
        ],
    },

    /* Extra versions for testing scrollbar */
    {
        versionTitle: "Version 2.1.0 (March 15, 2024)",
        description: "Minor update with UI/UX improvements.",
        items: [
            {
                title: "Improved Dashboard Layout",
                text: `We reorganized the main dashboard cards for more intuitive navigation.`,
            },
            {
                title: "Faster Load Times",
                text: `Reduced load times by optimizing API calls and caching frequently accessed data.`,
            },
        ],
    },
    {
        versionTitle: "Version 2.0.0 (January 10, 2024)",
        description: "Major release introducing CTX integration features.",
        items: [
            {
                title: "CTX Integration",
                text: `Deep integration with CTX environment for more seamless routing configurations.`,
            },
            {
                title: "Enhanced Security Layer",
                text: `We introduced advanced IP whitelisting, improved encryption, and real-time threat alerts.`,
            },
        ],
    },
    {
        versionTitle: "Version 1.9.0 (December 1, 2023)",
        description: "Stability improvements and bug fixes.",
        items: [
            {
                title: "Improved Error Handling",
                text: `Better error messages and logs, making it easier to troubleshoot issues.`,
            },
            {
                title: "Bug Fixes",
                text: `Resolved various minor UI bugs and fixed performance regressions introduced in 1.8.0.`,
            },
        ],
    },
];

const ChangelogModal: React.FC<ChangelogModalProps> = ({ isOpen, onClose }) => {
    return (
        <GenericModal
            isOpen={isOpen}
            onClose={onClose}
            title={
                <div className="flex items-center gap-2">
                    <IconChangelog className="h-5 w-5 text-semantic-text-secondary" />
                    <span>Changelog</span>
                </div>
            }
            size="lg"
            closeOnBackdropClick={true}
        >
            {/* 
        max-h-[70vh] -> Limita la altura del contenido para que no ocupe toda la pantalla
        overflow-y-auto -> Hace que aparezca un scroll interno si supera la altura
      */}
            <div className="max-h-[70vh] overflow-y-auto flex flex-col gap-4">
                {changelogData.map((version, index) => (
                    <div
                        key={index}
                        className="
              border border-semantic-border-secondary 
              p-4 rounded 
              bg-semantic-bg-primary
            "
                    >
                        {/* Título de versión */}
                        <h3 className="font-semibold text-semantic-text-primary">
                            {version.versionTitle}
                        </h3>
                        {/* Descripción breve */}
                        <p className="text-sm text-semantic-text-secondary mb-2">
                            {version.description}
                        </p>

                        {/* Lista de items */}
                        {version.items.map((item, idx) => (
                            <div key={idx} className="mb-3">
                                <p className="font-medium text-sm text-semantic-text-primary">
                                    {item.title}
                                </p>
                                <p className="text-xs text-semantic-text-secondary">
                                    {item.text}
                                </p>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </GenericModal>
    );
};

export default ChangelogModal;
